/**
 * Symbols Cache using IndexedDB
 *
 * Caches the 11MB symbols response from marketdata API for faster startup.
 * Uses IndexedDB for efficient storage of large datasets.
 */

const DB_NAME = "odin-sight-cache";
const DB_VERSION = 1;
const STORE_NAME = "symbols";
const CACHE_KEY = "symbols-data-v2";
const CACHE_DURATION = 1 * 60 * 60 * 1000; // 1 hours in milliseconds

/**
 * Open IndexedDB connection
 * @returns {Promise<IDBDatabase>}
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("[SymbolsCache] Failed to open IndexedDB:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
        console.log("[SymbolsCache] Created object store:", STORE_NAME);
      }
    };
  });
};

/**
 * Get cached symbols from IndexedDB
 * @returns {Promise<{data: Array, timestamp: number}|null>}
 */
export const getCachedSymbols = async () => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(CACHE_KEY);

      request.onsuccess = () => {
        const cached = request.result;

        if (!cached) {
          console.log("[SymbolsCache] No cached data found");
          resolve(null);
          return;
        }

        // Check if cache is still valid
        const age = Date.now() - cached.timestamp;
        const isValid = age < CACHE_DURATION;

        console.log(
          `[SymbolsCache] Cache found, age: ${Math.round(age / 1000 / 60)} minutes, valid: ${isValid}`,
        );

        resolve(cached);
      };

      request.onerror = () => {
        console.error("[SymbolsCache] Failed to read cache:", request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error("[SymbolsCache] Error getting cached symbols:", error);
    return null;
  }
};

/**
 * Save symbols to IndexedDB cache
 * @param {Array} symbols - Array of symbol objects
 * @returns {Promise<boolean>}
 */
export const setCachedSymbols = async (symbols) => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const cacheData = {
        data: symbols,
        timestamp: Date.now(),
      };

      const request = store.put(cacheData, CACHE_KEY);

      request.onsuccess = () => {
        console.log(
          `[SymbolsCache] Cached ${symbols.length} symbols (${(JSON.stringify(symbols).length / 1024 / 1024).toFixed(2)} MB)`,
        );
        resolve(true);
      };

      request.onerror = () => {
        console.error("[SymbolsCache] Failed to cache symbols:", request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error("[SymbolsCache] Error caching symbols:", error);
    return false;
  }
};

/**
 * Check if cached symbols are fresh (< CACHE_DURATION, currently 1 hour)
 * @returns {Promise<boolean>}
 */
export const isCacheFresh = async () => {
  try {
    const cached = await getCachedSymbols();

    if (!cached) {
      return false;
    }

    const age = Date.now() - cached.timestamp;
    return age < CACHE_DURATION;
  } catch (error) {
    console.error("[SymbolsCache] Error checking cache freshness:", error);
    return false;
  }
};

/**
 * Clear the symbols cache
 * @returns {Promise<boolean>}
 */
export const clearSymbolsCache = async () => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(CACHE_KEY);

      request.onsuccess = () => {
        console.log("[SymbolsCache] Cache cleared");
        resolve(true);
      };

      request.onerror = () => {
        console.error("[SymbolsCache] Failed to clear cache:", request.error);
        reject(request.error);
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error("[SymbolsCache] Error clearing cache:", error);
    return false;
  }
};
